import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../utils/components/ui/dialog";
import Loading from "../common/LoaderCommon";
import { useGetPurchaseReturnEntry } from "../../service/purchaseEntryReturnService";

const formatCurrency = (amount: number) =>
  `OMR ${new Intl.NumberFormat("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(amount)}`;

type PurchaseInvoiceDetailModalProps = {
  purchaseNumber: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function PurchaseInvoiceDetailModal({
  purchaseNumber,
  open,
  onOpenChange,
}: PurchaseInvoiceDetailModalProps) {
  const { data, isLoading, isError } = useGetPurchaseReturnEntry({
    purchaseNumber: purchaseNumber || undefined,
  });

  const invoice = data;
  const items = invoice?.purchaseEntryProductResponse ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-[980px] overflow-hidden rounded-[24px] border-none bg-white p-0 shadow-[0_24px_60px_rgba(0,0,0,0.16)] sm:max-w-[980px]">
        <div className="max-h-[90vh] overflow-y-auto p-5 sm:p-8">
          <DialogHeader className="mb-6">
            <DialogTitle className="text-[28px] font-semibold tracking-[-0.03em] text-[#1A5EF9]">
              Purchase Invoice Details
            </DialogTitle>
            <DialogDescription className="text-sm text-[#7A877F]">
              Review the purchase invoice and item breakdown for{" "}
              {purchaseNumber}.
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="py-16">
              <Loading />
            </div>
          ) : isError || !invoice ? (
            <div className="rounded-[18px] border border-[#F2D9D9] bg-[#FFF8F8] px-6 py-10 text-center text-sm text-[#B15454]">
              Unable to load invoice details right now.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  { label: "Purchase Number", value: invoice.purchaseNumber },
                  { label: "Vendor", value: invoice.vendorName },
                  { label: "Purchase Date", value: invoice.purchaseDate },
                  { label: "Items", value: String(invoice.itemCount) },
                ].map((field) => (
                  <div key={field.label}>
                    <p className="mb-2 text-sm text-[#8A948D]">{field.label}</p>
                    <div className="min-h-12 rounded-[12px] bg-[#F4F7FC] px-4 py-3 text-[15px] font-semibold text-[#354438] break-words">
                      {field.value || "-"}
                    </div>
                  </div>
                ))}
              </div>

              <div className="overflow-hidden rounded-[20px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
                <div className="overflow-x-auto">
                  <table className="min-w-[760px] w-full text-sm text-[#43536A]">
                    <thead>
                      <tr className="bg-[#F5F8FD] text-left text-xs font-semibold text-[#31425E] uppercase tracking-wide">
                        <th className="px-4 py-3">#</th>
                        <th className="px-4 py-3">Product</th>
                        <th className="px-4 py-3">Qty</th>
                        <th className="px-4 py-3">Unit</th>
                        <th className="px-4 py-3">Unit Price</th>
                        <th className="px-4 py-3">Tax</th>
                        <th className="px-4 py-3">Discount</th>
                        <th className="px-4 py-3">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr
                          key={item.id}
                          className="border-t border-[#EEF2FA] hover:bg-[#FAFCFF] transition-colors"
                        >
                          <td className="px-4 py-3 text-[#8A9BB5]">
                            {index + 1}
                          </td>
                          <td className="px-4 py-3 font-medium text-[#23324D]">
                            {item.productName}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {item.quantity}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {item.unit || "-"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {item.unitPrice?.toFixed?.(3) ?? item.unitPrice}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {item.tax}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {item.discount}
                          </td>
                          <td className="px-4 py-3 font-semibold text-[#23324D] whitespace-nowrap">
                            {formatCurrency(item.totalAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-[20px] border border-[#E8EEF9] bg-[#F6F9FF] p-4 text-sm text-[#52627C] sm:p-5">
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    {
                      label: "Net Total",
                      value: formatCurrency(invoice.totalAmount),
                    },
                    {
                      label: "Amount Paid",
                      value: formatCurrency(invoice.amountPaid),
                    },
                    {
                      label: "Balance Amount",
                      value: formatCurrency(invoice.balanceAmount),
                    },
                    { label: "Tax", value: formatCurrency(invoice.totalTax) },
                    {
                      label: "Discount",
                      value: formatCurrency(invoice.totalDiscount),
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="min-h-[86px] rounded-[14px] bg-white p-4 shadow-[0_8px_24px_rgba(26,94,249,0.08)]"
                    >
                      <p className="text-xs text-[#8A948D]">{item.label}</p>
                      <p className="mt-2 text-lg font-semibold text-[#1A5EF9]">
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PurchaseInvoiceDetailModal;
